/**
 * A storage capacity — power and energy together.
 *
 * PRD §9.5 requires both: `20 MW / 40 MWh`. A storage asset described by power
 * alone says nothing about how long it can sustain that power, which is the
 * figure that decides whether a pilot is viable. The PRD notes that a reader
 * who sees MW where MWh was meant stops trusting the rest of the record.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.16, PRD §9.5
 */
import { cn } from '../../lib/cn';
import { formatCapacity, type Precision } from '../../lib/format';

export interface CapacityProps {
  /** Power in megawatts. */
  mw?: number | null;
  /** Energy in megawatt-hours. */
  mwh?: number | null;
  precision?: Precision;
  emptyText?: string;
  className?: string;
}

export function Capacity({
  mw,
  mwh,
  precision = 'table',
  emptyText = '—',
  className,
}: CapacityProps): React.JSX.Element {
  const formatted = formatCapacity({ mw, mwh }, precision);

  return (
    <span className={cn('tabular-nums', className)}>
      {formatted === '' ? emptyText : formatted}
    </span>
  );
}
